/**
 * ThreePanel — the canonical content layout.
 *
 * PRIMARY surface (60–65% width: current task / work-queue rows / structured
 * forms / primary CTA) on the left, RIGHT CONTEXT panel (35–40%: patient
 * summary, case history, related flags, external links) on the warm surface
 * (#F4F3F1) on the right. When `rightContext` is omitted, primary takes the
 * full width — pages without context use a single column.
 *
 * Stacks to a single column on narrow viewports.
 */
import type { CSSProperties, ReactNode } from 'react';
import { colors, radius } from '../theme';

export interface ThreePanelProps {
  primary: ReactNode;
  /** Right supporting-context content. Omit for a single-column page. */
  rightContext?: ReactNode;
  style?: CSSProperties;
}

export function ThreePanel({ primary, rightContext, style }: ThreePanelProps) {
  if (!rightContext) {
    return <div style={{ ...single, ...style }}>{primary}</div>;
  }
  return (
    <div style={{ ...grid, ...style }}>
      <div style={primaryCol}>{primary}</div>
      <aside style={rightCol}>{rightContext}</aside>
    </div>
  );
}

const single: CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};
const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 1fr)',
  gap: 20,
  alignItems: 'start',
  width: '100%',
};
const primaryCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  minWidth: 0,
};
const rightCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: 16,
  position: 'sticky',
  top: 16,
};
